/**
 * パーティクル用のテクスチャを生成するクラス
 */
export class ParticleTexture {
    /**
     * 基本的なパーティクルテクスチャを生成する
     * @param {Phaser.Scene} scene - テクスチャを生成するシーン
     * @param {Object} options - テクスチャ生成オプション
     * @param {string} options.textureName - 生成するテクスチャの名前（デフォルト: 'particle'）
     * @param {number} options.pixelWidth - パーティクルのピクセル幅（デフォルト: 1）
     * @returns {boolean} テクスチャ生成の成否
     */
    static create(scene, options = {}) {
        const textureName = options.textureName || 'particle';
        const pixelWidth = options.pixelWidth || 1;
        
        try {
            // 既にテクスチャが存在する場合は作成しない
            if (scene.textures.exists(textureName)) {
                console.log(`ParticleTexture: ${textureName}テクスチャは既に存在します`);
                return true;
            }
            
            // 単純なパーティクルテクスチャを生成
            scene.textures.generate(textureName, { data: ['1'], pixelWidth });
            
            console.log(`ParticleTexture: ${textureName}テクスチャを生成しました`);
            return true;
        } catch (error) {
            console.error(`ParticleTexture: ${textureName}テクスチャの生成中にエラーが発生しました`, error);
            return false;
        }
    }

    /**
     * 炎のパーティクルテクスチャを生成する
     * @param {Phaser.Scene} scene - テクスチャを生成するシーン
     * @param {Object} options - テクスチャ生成オプション
     * @param {string} options.textureName - 生成するテクスチャの名前（デフォルト: 'flame'）
     * @returns {boolean} テクスチャ生成の成否
     */
    static createFlame(scene, options = {}) {
        const textureName = options.textureName || 'flame';
        
        try {
            // 既にテクスチャが存在する場合は作成しない
            if (scene.textures.exists(textureName)) {
                console.log(`ParticleTexture: ${textureName}テクスチャは既に存在します`);
                return true;
            }
            
            // 炎のようなテクスチャを生成（複数の円を重ねる）
            const size = 32; // テクスチャサイズ
            const centerX = size / 2;
            const centerY = size / 2;
            
            // レンダーテクスチャを作成
            const rt = scene.add.renderTexture(0, 0, size, size);
            
            // 複数の円を描画して炎のような効果を作る
            const graphics = scene.add.graphics();
            
            // 外側の円（赤）- 半透明
            graphics.fillStyle(0xff0000, 0.5);
            graphics.fillCircle(centerX, centerY, size / 2);
            
            // 中間の円（オレンジ）
            graphics.fillStyle(0xff6600, 0.7);
            graphics.fillCircle(centerX, centerY, size / 3);
            
            // 内側の円（黄色）
            graphics.fillStyle(0xffff00, 0.9);
            graphics.fillCircle(centerX, centerY, size / 4);
            
            // 中心の円（白）
            graphics.fillStyle(0xffffff, 1);
            graphics.fillCircle(centerX, centerY, size / 8);
            
            // レンダーテクスチャに描画
            rt.draw(graphics);
            
            // テクスチャとして保存
            rt.saveTexture(textureName);
            
            // 一時オブジェクトを破棄
            graphics.destroy();
            rt.destroy();
            
            console.log(`ParticleTexture: ${textureName}テクスチャを生成しました`);
            return true;
        } catch (error) {
            console.error(`ParticleTexture: ${textureName}テクスチャの生成中にエラーが発生しました`, error);
            return false;
        }
    }
    
    /**
     * キラキラ効果のパーティクルテクスチャを生成する
     * @param {Phaser.Scene} scene - テクスチャを生成するシーン
     * @param {Object} options - テクスチャ生成オプション
     * @param {string} options.textureName - 生成するテクスチャの名前（デフォルト: 'sparkle'）
     * @returns {boolean} テクスチャ生成の成否
     */
    static createSparkle(scene, options = {}) {
        const textureName = options.textureName || 'sparkle';
        
        try {
            // 既にテクスチャが存在する場合は作成しない
            if (scene.textures.exists(textureName)) {
                console.log(`ParticleTexture: ${textureName}テクスチャは既に存在します`);
                return true;
            }
            
            // キラキラ効果のテクスチャを生成（星形）
            const graphics = scene.add.graphics();
            const size = 16; // テクスチャサイズ
            const centerX = size / 2;
            const centerY = size / 2;
            const outerRadius = size / 2;
            const innerRadius = size / 4;
            const spikes = 4; // 星の尖った部分の数
            
            graphics.clear();
            graphics.fillStyle(0xffffff, 1); // 白色
            
            // 星形を描画
            graphics.beginPath();
            
            let rot = Math.PI / 2 * 3;
            let x = centerX;
            let y = centerY;
            const step = Math.PI / spikes;
            
            graphics.moveTo(centerX, centerY - outerRadius);
            
            for (let i = 0; i < spikes; i++) {
                x = centerX + Math.cos(rot) * outerRadius;
                y = centerY + Math.sin(rot) * outerRadius;
                graphics.lineTo(x, y);
                rot += step;
                
                x = centerX + Math.cos(rot) * innerRadius;
                y = centerY + Math.sin(rot) * innerRadius;
                graphics.lineTo(x, y);
                rot += step;
            }
            
            graphics.lineTo(centerX, centerY - outerRadius);
            graphics.closePath();
            graphics.fillPath();
            
            // テクスチャとして保存
            const rt = scene.add.renderTexture(0, 0, size, size);
            rt.draw(graphics);
            rt.saveTexture(textureName);
            
            // 一時オブジェクトを破棄
            graphics.destroy();
            rt.destroy();
            
            console.log(`ParticleTexture: ${textureName}テクスチャを生成しました`);
            return true;
        } catch (error) {
            console.error(`ParticleTexture: ${textureName}テクスチャの生成中にエラーが発生しました`, error);
            return false;
        }
    }
    
    /**
     * 重り用のパーティクルテクスチャを生成する
     * @param {Phaser.Scene} scene - テクスチャを生成するシーン
     * @param {Object} options - テクスチャ生成オプション
     * @param {string} options.textureName - 生成するテクスチャの名前（デフォルト: 'weight'）
     * @returns {boolean} テクスチャ生成の成否
     */
    static createWeight(scene, options = {}) {
        const textureName = options.textureName || 'weight';
        return this.create(scene, { textureName, pixelWidth: 1 });
    }
    
    /**
     * ターゲット用のパーティクルテクスチャを生成する
     * @param {Phaser.Scene} scene - テクスチャを生成するシーン
     * @param {Object} options - テクスチャ生成オプション
     * @param {string} options.textureName - 生成するテクスチャの名前（デフォルト: 'target'）
     * @returns {boolean} テクスチャ生成の成否
     */
    static createTarget(scene, options = {}) {
        const textureName = options.textureName || 'target';
        return this.create(scene, { textureName, pixelWidth: 1 });
    }
    
    /**
     * 全てのパーティクルテクスチャを生成する
     * @param {Phaser.Scene} scene - テクスチャを生成するシーン
     * @returns {boolean} テクスチャ生成の成否
     */
    static createAll(scene) {
        try {
            // 各種パーティクルテクスチャを生成
            const weightSuccess = this.createWeight(scene);
            const targetSuccess = this.createTarget(scene);
            const flameSuccess = this.createFlame(scene);
            const sparkleSuccess = this.createSparkle(scene);
            
            // 全てのテクスチャが正常に生成されたかチェック
            const allSuccess = weightSuccess && targetSuccess && flameSuccess && sparkleSuccess;
            
            if (allSuccess) {
                console.log('ParticleTexture: 全てのパーティクルテクスチャを生成しました');
            } else {
                console.error('ParticleTexture: 一部のパーティクルテクスチャの生成に失敗しました');
            }
            
            return allSuccess;
        } catch (error) {
            console.error('ParticleTexture: パーティクルテクスチャの生成中にエラーが発生しました', error);
            return false;
        }
    }
}
